import type { RiskType, SafetyMessageOverride, SafetyPolicyType } from "@unbox/api";
import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  InfoCircle,
  Plus,
  Share03,
  Shield02,
  ShieldZap,
  XClose,
} from "@untitledui/icons";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { QuarantineTag } from "@/components/assurance/badges/ad-campaign-badge";
import { Button } from "@/components/base/buttons/button";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { Transcript } from "@/components/icons/transcript";
import { tierToSeverityLabel } from "@/components/safety-config/risk-groups";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { VideoDetails } from "@/components/video-details";
import type { LogActionType } from "@/lib/assurance/action-types";
import { CreatorDetails } from "@/pages/creators/creator-details";
import { VideoEmbed } from "@/pages/videos/video-embed";
import { cx } from "@/utils/cx";
import { getDetectedLanguageLabel } from "@/utils/language";
import { buildTikTokVideoUrl, getRiskTypeIcon } from "../utils";
import { CustomRiskRequest } from "./CustomRiskRequest";
import type { RiskEvidence } from "./RiskCard";
import { RiskCard } from "./RiskCard";
import { WhatHappensNext } from "./what-happens-next";

export type Risk = {
  id: string;
  // The catalog risk the flag was raised for; absent for uncatalogued "Other" flags.
  riskId?: string | null;
  type: RiskType;
  title: string;
  evidence: RiskEvidence[];
  reviewReason?: string | null;
  severity?: "critical" | "high" | "medium" | "low";
  description?: string;
  // True when a reviewer added this risk manually rather than the model detecting it —
  // see resolveCustomRiskAttribution.
  isCustom?: boolean;
  flaggedByName?: string;
  flaggedAt?: string;
};

export type ActionStep = {
  text: string;
  // The sequence step this entry came from. Identifies which step an edited message belongs
  // to; absent on synthetic entries (the "Wait Nd" and branch-arm labels).
  step?: number;
  isSubtle?: boolean;
  // True when this step has a message preview available (clickable, shows an eye icon).
  hasMessagePreview?: boolean;
  // Raw message content (template content or custom message), unresolved — only set when hasMessagePreview.
  messageContent?: string;
};

export type VideoItem = {
  id: string;
  title: string;
  creator: string;
  creatorId?: string;
  views: string;
  postedAt: string;
  postedAtDate: string;
  gmv: string;
  flagsCountDetail?: { flagName: string; severity: number }[];
  // Most severe non-dismissed flag + total count, for the Needs Review table's
  // "Risks" column (fullWidth only) — same shape as With Creator/Risk Resolved.
  topFlags?: { name: string; severity: "critical" | "high" | "medium" | "low" } | null;
  flagCount?: number;
  risks: Risk[];
  actionSteps: ActionStep[];
  urlFirstFrame?: string | null;
  videoUrl?: string | null;
  isInAdCampaign?: boolean;
  unableToAccess?: boolean;
  isQuarantined?: boolean;
  transcript?: string | null;
  detectedLanguage?: string | null;
  englishTranscript?: string | null;
};

export type RiskState = {
  dismissed: boolean;
  rejectReason: string;
  rejectNote: string;
};

export type PolicyOption = {
  id: string;
  label: string;
  type: SafetyPolicyType;
  sequenceId: string;
};

export type ReviewSubmission = {
  videoId: string;
  riskStates: Record<string, RiskState>;
  // The safety policy chosen to run (normal videos with risks to action).
  policyId?: string;
  // For "unable to access" videos there's no policy — resolve by logging no action.
  logActionType?: LogActionType;
  // Copy the reviewer rewrote for the policy's message steps, for this video only.
  messageOverrides?: SafetyMessageOverride[];
};

export type MessageDraft = {
  policyId: string | undefined;
  overrides: Record<number, string>;
};

type VideoDetailProps = {
  video: VideoItem;
  current: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  onSubmit: (submission: ReviewSubmission) => void;
  isSubmitting?: boolean;
  // The merchant's configured safety policies, already filtered for eligibility
  // (e.g. metadata-edit only when a reviewed risk is required_text).
  policyOptions: PolicyOption[];
  // The policy that would run by default (highest-priority among the video's
  // risks). Pre-selected; the reviewer can change it.
  defaultPolicyId?: string;
  messageDraft?: MessageDraft;
  onMessageDraftChange: (draft: MessageDraft | undefined) => void;
};

const buildInitialRiskStates = (risks: Risk[]): Record<string, RiskState> =>
  Object.fromEntries(risks.map((r) => [r.id, { dismissed: false, rejectReason: "Other", rejectNote: "" }]));

// Shows the riskiest severity present in the last 30 days and how many flags are at that
// exact level — e.g. 1 critical + 3 minor flags shows "1 Critical", not every flag's count.
// Tiers 1/2 (critical/high) get their own label; 3/4 (medium/low) are grouped as "minor",
// matching tierToSeverity's own bucketing.
const summarizeRecentFlags = (
  flags: { flagName: string; severity: number }[],
): { label: string; count: number } | null => {
  if (flags.length === 0) return null;
  const topSeverity = Math.min(...flags.map((f) => f.severity));
  if (topSeverity === 1) return { label: tierToSeverityLabel(1), count: flags.filter((f) => f.severity === 1).length };
  if (topSeverity === 2) return { label: tierToSeverityLabel(2), count: flags.filter((f) => f.severity === 2).length };
  return { label: "minor", count: flags.filter((f) => f.severity === 3 || f.severity === 4).length };
};

// Calibration point: at this card size the video embed reads correctly at 204.75px
// wide (364px tall, 9:16). The resize-driven width below scales proportionally from
// here, so at the reference size it renders pixel-identical to the old fixed values.
const REFERENCE_CARD_WIDTH = 672;
const REFERENCE_CARD_HEIGHT = 517.59;
const REFERENCE_VIDEO_WIDTH = 204.75;

const verticalInset = (el: Element) => {
  const style = getComputedStyle(el);
  return (
    parseFloat(style.paddingTop) +
    parseFloat(style.paddingBottom) +
    parseFloat(style.borderTopWidth) +
    parseFloat(style.borderBottomWidth)
  );
};

const rowGap = (el: Element) => parseFloat(getComputedStyle(el).rowGap) || 0;

export const VideoDetail = ({
  video,
  current,
  total,
  onPrev,
  onNext,
  onClose,
  onSubmit,
  isSubmitting,
  policyOptions,
  defaultPolicyId,
  messageDraft,
  onMessageDraftChange,
}: VideoDetailProps) => {
  const unableToAccess = video.unableToAccess === true;
  const recentFlagsSummary = summarizeRecentFlags(video.flagsCountDetail ?? []);
  const [addRiskOpen, setAddRiskOpen] = useState(false);
  // True while a half-filled missed risk is holding back clicks, which greys out the rest of the column.
  const [addRiskBlocking, setAddRiskBlocking] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const topBarRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const videoColumnRef = useRef<HTMLDivElement>(null);
  const transcriptTriggerRef = useRef<HTMLButtonElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);
  const [cardSize, setCardSize] = useState({ width: REFERENCE_CARD_WIDTH, height: REFERENCE_CARD_HEIGHT });
  const [videoMaxHeight, setVideoMaxHeight] = useState(Number.POSITIVE_INFINITY);
  const hasTranscript = Boolean(video.transcript);
  const [risksOpen, setRisksOpen] = useState(true);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [selectedCreatorId, setSelectedCreatorId] = useState<string | undefined>();
  const [selectedVideoId, setSelectedVideoId] = useState<string | undefined>();
  const [policyId, setPolicyId] = useState<string | undefined>(defaultPolicyId ?? policyOptions[0]?.id);
  // True only once the reviewer explicitly opens "Run different policy" — gates whether
  // policyId is sent on submit, or left undefined for the backend to auto-resolve from
  // confirmed risks (the client-computed default can go stale after risks are dismissed).
  const [policyOverridden, setPolicyOverridden] = useState(false);
  const messageOverrides = messageDraft && messageDraft.policyId === policyId ? messageDraft.overrides : {};
  // Custom risks are view-only — never let them drive a policy action.
  const [riskStates, setRiskStates] = useState<Record<string, RiskState>>(() =>
    buildInitialRiskStates(video.risks.filter((r) => !r.isCustom)),
  );

  // Risks a reviewer added manually (rather than the model detecting them) carry
  // isCustom from resolveCustomRiskAttribution — render those via CustomRiskRequest
  // instead of the AI-detected loop, so the "custom" look survives a page reload.
  const existingCustomRisks = video.risks
    .filter((risk) => risk.isCustom)
    .map((risk) => ({
      id: risk.id,
      title: risk.title,
      description: risk.evidence[0]?.text ?? "",
      severity: risk.severity ?? ("low" as const),
      flaggedByName: risk.flaggedByName,
      flaggedAt: risk.flaggedAt,
    }));
  // A free-text risk the reviewer added maps to no policy, so it can't keep one running.
  const actionableRisks = video.risks.filter((r) => !r.isCustom || r.riskId);
  const allRisksDismissed =
    !unableToAccess && actionableRisks.length > 0 && actionableRisks.every((r) => riskStates[r.id]?.dismissed);

  // Rebuild risk states when the set of risks changes — including when the video's
  // detail (and thus its flags) loads in after the row is first rendered. Keyed on
  // the risk ids so user dismiss/confirm choices aren't wiped on unrelated rerenders.
  // Custom risks are view-only — never let them drive a policy action.
  const riskIdsKey = video.risks.map((r) => r.id).join(",");
  useEffect(() => {
    setRiskStates(buildInitialRiskStates(video.risks.filter((r) => !r.isCustom)));
  }, [riskIdsKey]);

  // Reset the manual override only when switching videos — not on every dismiss/confirm toggle,
  // or cancelling all risks would silently discard a reviewer's manual policy choice.
  useEffect(() => {
    setPolicyOverridden(false);
  }, [video.id]);

  // Pre-select the policy that would run: the video's default, unless every risk has been
  // dismissed, in which case "No action required" takes over (nothing will actually run either
  // way — see TabReview's allDismissed check — this just keeps the UI honest about it). Skipped
  // while the reviewer has manually picked a policy via "Run different policy" — UNLESS every
  // risk ends up dismissed, in which case the manual pick is overridden back to "No action
  // required" too: TabReview forces startPolicies to false whenever allDismissed regardless of
  // policyId, so preserving the manual choice here would show "Save & take action" for an action
  // that will never run.
  useEffect(() => {
    if (policyOverridden && !allRisksDismissed) return;
    const noActionPolicyId = policyOptions.find((p) => p.type === "no_action_needed")?.id;
    const resolved =
      allRisksDismissed && noActionPolicyId ? noActionPolicyId : (defaultPolicyId ?? policyOptions[0]?.id);
    setPolicyId(resolved);
  }, [video.id, defaultPolicyId, policyOptions, allRisksDismissed, policyOverridden]);

  useEffect(() => {
    if (transcriptOpen) {
      setTranscriptOpen(false);
    }
  }, [video.id]); // reset open/closed state when switching videos

  // Tracks the card's own box so the video embed can scale with it — only updates
  // on an actual resize, never on unrelated re-renders.
  //
  // The card renders collapsed while its content (video, risk list) is still loading,
  // then grows once it lands — resizing the video in lockstep with that reads as a
  // jarring shrink-then-grow flash. Holding videoWidth steady for the first beat lets
  // that initial layout churn settle before we start tracking real resizes: the latest
  // measurement is tracked silently in a ref the whole time, then committed once when
  // the settle window ends, so we don't need a further resize to pick up where things
  // actually landed.
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    let settled = false;
    const latestSize = { width: REFERENCE_CARD_WIDTH, height: REFERENCE_CARD_HEIGHT };
    const settleTimer = setTimeout(() => {
      settled = true;
      setCardSize({ ...latestSize });
    }, 1000);
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      latestSize.width = width;
      latestSize.height = height;
      if (settled) setCardSize({ width, height });
    });
    observer.observe(el);
    return () => {
      clearTimeout(settleTimer);
      observer.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    const panel = panelRef.current;
    const card = cardRef.current;
    const topBar = topBarRef.current;
    const content = contentRef.current;
    const videoColumn = videoColumnRef.current;
    const pagination = paginationRef.current;
    if (!panel || !card || !topBar || !content || !videoColumn || !pagination) return;
    const trigger = transcriptTriggerRef.current;
    const measure = () => {
      const cardMaxHeight = panel.clientHeight - pagination.offsetHeight - rowGap(panel);
      const videoColumnMaxHeight = cardMaxHeight - verticalInset(card) - topBar.offsetHeight - verticalInset(content);
      const transcriptRowHeight =
        trigger?.parentElement != null
          ? trigger.offsetHeight + verticalInset(trigger.parentElement) + rowGap(videoColumn)
          : 0;
      setVideoMaxHeight(Math.max(0, videoColumnMaxHeight - transcriptRowHeight));
    };
    measure();
    const observer = new ResizeObserver(measure);
    for (const el of [panel, topBar, pagination, trigger]) if (el) observer.observe(el);
    return () => observer.disconnect();
  }, [hasTranscript]);

  // Scaled off whichever axis is more constrained, so the video keeps its 9:16 shape
  // (CSS aspect-ratio derives height from this) and never overflows either dimension.
  const videoWidth = useMemo(() => {
    const scale = Math.min(cardSize.width / REFERENCE_CARD_WIDTH, cardSize.height / REFERENCE_CARD_HEIGHT);
    return Math.min(REFERENCE_VIDEO_WIDTH * scale, (videoMaxHeight * 9) / 16);
  }, [cardSize.width, cardSize.height, videoMaxHeight]);

  const updateRisk = (id: string, patch: Partial<RiskState>) =>
    setRiskStates((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const handleRiskRemoved = (flagId: string) => {
    setRiskStates((prev) => {
      const next = { ...prev };
      delete next[flagId];
      return next;
    });
  };
  // Drives the footer button's label — "Save" whenever the policy about to be sent is "No action
  // required", whether it got there automatically (all risks dismissed) or the reviewer picked it
  // manually via "Run different policy".
  const isNoActionPolicySelected = policyOptions.find((p) => p.id === policyId)?.type === "no_action_needed";

  return (
    <>
      <div ref={panelRef} className="min-w-0 w-[calc(66%-1rem)] flex flex-col gap-[32px] h-full overflow-hidden">
        <div
          ref={cardRef}
          className="border border-border-primary rounded-md bg-white flex flex-col min-h-0 max-h-full"
        >
          {/* Top bar */}
          <div ref={topBarRef} className="flex items-end gap-4 pt-4 px-6 shrink-0 pb-0">
            <div className="flex flex-col w-1/2 gap-1 min-w-0">
              <Tooltip title={video.title}>
                <TooltipTrigger
                  className="inline-flex w-full min-w-0 justify-start"
                  onPress={() => setSelectedVideoId(video.id)}
                >
                  <span className="text-base font-medium text-primary truncate underline decoration-dotted leading-6 cursor-pointer hover:text-secondary transition-colors">
                    {video.title}
                  </span>
                </TooltipTrigger>
              </Tooltip>
              <div className="flex items-center gap-1.5 min-w-0">
                <button
                  type="button"
                  className="text-sm text-tertiary underline decoration-dotted whitespace-nowrap cursor-pointer hover:text-secondary transition-colors bg-transparent border-none p-0 shrink-0"
                  onClick={() => video.creatorId && setSelectedCreatorId(video.creatorId)}
                >
                  {video.creator}
                </button>
                {recentFlagsSummary && (
                  <>
                    <span className="text-sm text-tertiary shrink-0">•</span>
                    <Tooltip
                      title={`${recentFlagsSummary.count} ${recentFlagsSummary.label} violations in the last 30 days`}
                    >
                      <TooltipTrigger className="inline-flex min-w-0 justify-start">
                        <span className="text-sm text-tertiary truncate">
                          {recentFlagsSummary.count} {recentFlagsSummary.label} violations in the last 30 days
                        </span>
                      </TooltipTrigger>
                    </Tooltip>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-6 w-1/2 min-w-0 text-sm leading-5">
              {[
                { label: "Views", value: video.views },
                { label: "GMV (30d)", value: video.gmv },
                { label: "Posted", value: video.postedAtDate },
              ].map(({ label, value }) => (
                <div key={label} className="flex flex-col items-center min-w-0">
                  <span className="font-medium text-primary truncate">{value}</span>
                  <span className="text-tertiary truncate">{label}</span>
                </div>
              ))}
              <div className="flex w-[24px] h-[36px]">
                <button
                  type="button"
                  aria-label="Close video detail"
                  className="h-[10px] text-tertiary hover:text-secondary transition-colors bg-transparent border-none p-0 cursor-pointer"
                  onClick={onClose}
                >
                  <XClose className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Content: video + risk/action panels */}
          <div ref={contentRef} className="flex flex-row p-6 overflow-hidden min-h-0">
            {/* Video player */}
            <div
              ref={videoColumnRef}
              className="flex flex-col gap-[8px] overflow-auto shrink-0 overflow-x-hidden min-w-0"
              style={{ width: videoWidth }}
            >
              <div className="relative flex-shrink-0 aspect-[9/16] rounded-lg bg-gray-100 overflow-hidden">
                <VideoEmbed
                  width={`${videoWidth}px`}
                  url={video.videoUrl}
                  videoId={video.id}
                  thumbnail={video.urlFirstFrame}
                  noScroll
                />
                {video.isQuarantined && <QuarantineTag className="absolute bottom-2 left-2" />}
              </div>

              {video.transcript && (
                <Collapsible open={transcriptOpen} onOpenChange={setTranscriptOpen}>
                  <div className="flex flex-col gap-3 py-1">
                    <CollapsibleTrigger ref={transcriptTriggerRef} className="flex w-full items-center gap-1.5">
                      <Transcript className="w-5 h-5 text-secondary shrink-0" />
                      <span className="text-sm font-medium text-secondary flex-1 min-w-0 truncate text-left">
                        {video.detectedLanguage
                          ? `Video Transcript (${getDetectedLanguageLabel(video.detectedLanguage)})`
                          : "Video Transcript"}
                      </span>
                      {transcriptOpen ? (
                        <ChevronUp className="w-5 h-5 text-secondary shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-secondary shrink-0" />
                      )}
                    </CollapsibleTrigger>
                    <CollapsibleContent className="max-w-full">
                      <p className="text-sm text-quaternary font-normal leading-relaxed whitespace-pre-wrap">
                        {video.englishTranscript && video.englishTranscript !== video.transcript ? (
                          <>
                            {video.englishTranscript}
                            <br />
                            <br />
                            <em>{video.transcript}</em>
                          </>
                        ) : (
                          video.transcript
                        )}
                      </p>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              )}
            </div>

            {/* Risk + action sections — scrolls independently */}
            <div className="flex flex-col flex-1 min-w-0 gap-4 overflow-y-auto ml-[32px]">
              {/* Confirm Identified Risks */}
              <Collapsible open={risksOpen} onOpenChange={setRisksOpen}>
                <div className="bg-[#fcf6f1] border-none flex flex-col gap-4 rounded-md p-3">
                  <CollapsibleTrigger className="flex w-full items-center gap-1.5">
                    <ShieldZap className="w-5 h-5 text-secondary" />
                    <span className="text-sm font-medium text-secondary flex-1 text-left">
                      {unableToAccess ? "Risk Unknown" : "Confirm Identified Risks"}
                    </span>
                    {risksOpen ? (
                      <ChevronUp className="w-5 h-5 text-secondary" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-secondary" />
                    )}
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    {unableToAccess ? (
                      <div className="bg-utility-warning-50 border border-utility-warning-200 rounded-md p-4 flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <Shield02 className="w-5 h-5 text-utility-warning-600 shrink-0" />
                          <span className="text-sm font-semibold text-primary flex-1">Unable to access video</span>
                          <InfoCircle className="w-4 h-4 text-utility-warning-600 shrink-0" />
                        </div>
                        <p className="text-sm text-tertiary leading-5">
                          We were unable to access this video to review the risk. Try logging in with your own TikTok
                          account to access and review.
                        </p>
                        <a
                          href={buildTikTokVideoUrl(video.creatorId, video.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline mt-1"
                        >
                          View on TikTok <Share03 className="w-4 h-4" />
                        </a>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-3">
                        {video.risks
                          .filter((risk) => !risk.isCustom)
                          .map((risk) => (
                            <div key={risk.id} className={cx("transition-opacity", addRiskBlocking && "opacity-40")}>
                              <RiskCard
                                key={risk.id}
                                icon={getRiskTypeIcon(risk.type)}
                                title={risk.title}
                                severity={risk.severity ?? "low"}
                                tooltipInfo={risk.description}
                                evidence={risk.evidence}
                                reviewReason={risk.reviewReason}
                                riskId={risk.riskId}
                                dismissal={
                                  riskStates[risk.id]?.dismissed
                                    ? { reason: riskStates[risk.id].rejectReason, note: riskStates[risk.id].rejectNote }
                                    : undefined
                                }
                                onDismiss={({ reason, note }) =>
                                  updateRisk(risk.id, { dismissed: true, rejectReason: reason, rejectNote: note })
                                }
                                onConfirm={() => updateRisk(risk.id, { dismissed: false })}
                              />
                            </div>
                          ))}
                        <CustomRiskRequest
                          videoId={video.id}
                          existingCustomRisks={existingCustomRisks}
                          open={addRiskOpen}
                          onOpenChange={setAddRiskOpen}
                          onRiskRemoved={handleRiskRemoved}
                          onBlockingChange={setAddRiskBlocking}
                        />
                        {!addRiskOpen && (
                          <Button
                            color="secondary"
                            size="sm"
                            iconLeading={<Plus className="w-4 h-4 text-quaternary" />}
                            onClick={() => setAddRiskOpen(true)}
                            className="!bg-[#FCF6F1]"
                          >
                            Add Risk
                          </Button>
                        )}
                      </div>
                    )}
                  </CollapsibleContent>
                </div>
              </Collapsible>

              {/* What happens next */}
              <div className={cx("transition-opacity", addRiskBlocking && "opacity-40")}>
                <WhatHappensNext
                  key={video.id}
                  policyOptions={policyOptions}
                  policyId={policyId}
                  defaultPolicyId={defaultPolicyId}
                  onPolicyChange={setPolicyId}
                  videoId={video.id}
                  creatorId={video.creatorId}
                  onOverrideChange={setPolicyOverridden}
                  allRisksDismissed={allRisksDismissed}
                  messageOverrides={messageOverrides}
                  onMessageOverrideChange={(step, content) => {
                    const { [step]: _dropped, ...rest } = messageOverrides;
                    const overrides = content === null ? rest : { ...rest, [step]: content };
                    onMessageDraftChange(Object.keys(overrides).length > 0 ? { policyId, overrides } : undefined);
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Pagination */}
        <div ref={paginationRef} className="border-none flex items-center p-0 shrink-0">
          <div className="flex flex-1 items-center justify-between">
            <Button
              color="secondary"
              size="sm"
              iconLeading={<ArrowLeft className="w-5 h-5" />}
              onClick={onPrev}
              isDisabled={current <= 1}
            />
            <span className="text-sm font-medium text-secondary">
              {current} of {total}
            </span>
            <Button
              color="secondary"
              size="sm"
              iconLeading={<ArrowRight className="w-5 h-5" />}
              onClick={onNext}
              isDisabled={current >= total}
            />
          </div>
          <div className="flex gap-1.5 ml-8">
            <Button
              color="primary"
              size="sm"
              isLoading={isSubmitting}
              onClick={() =>
                onSubmit(
                  unableToAccess
                    ? {
                        videoId: video.id,
                        riskStates,
                        logActionType: "no_action_required",
                      }
                    : {
                        videoId: video.id,
                        riskStates,
                        policyId: policyOverridden ? policyId : undefined,
                        messageOverrides: Object.entries(messageOverrides).map(([step, content]) => ({
                          step: Number(step),
                          content,
                        })),
                      },
                )
              }
            >
              {isNoActionPolicySelected ? "Save" : "Save & take action"}
            </Button>
          </div>
        </div>
      </div>
      <CreatorDetails id={selectedCreatorId} onClose={() => setSelectedCreatorId(undefined)} />
      <VideoDetails isAssurance={true} id={selectedVideoId} onClose={() => setSelectedVideoId(undefined)} />
    </>
  );
};
